import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

/**
 * A link in content somebody typed or pasted: a chat message, a task
 * description, a knowledge-base page, a link preview card.
 *
 * Every one of those used to be `<a target="_blank">`, which is right for a
 * link to somewhere else and wrong for a link back into Firmio. Installed as
 * an app, `target="_blank"` on our own URL opens a SECOND app window — and a
 * third, and a fourth, one per click — instead of going to the page. Even in
 * a browser tab it forces a full reload of the whole SPA to reach a route the
 * router could have shown instantly.
 *
 * So: our own pages navigate in place, everything else still opens away from
 * the app, where it belongs.
 */

/**
 * The in-app path for `href`, or null when it is not one of our own pages.
 *
 * Deliberately excludes `/api/…`: those are the storage proxy's download URLs
 * and other server responses. They are same-origin but they are not routes —
 * handing one to the router would show the app's "page not found" instead of
 * downloading the file.
 */
export function internalPath(href: string): string | null {
  if (typeof window === 'undefined') return null;
  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  if (url.pathname.startsWith('/api/')) return null;
  // Rebuilt rather than passed through, so only the parts a route can use
  // survive — and so an absolute URL and the same link written relatively
  // behave identically.
  return `${url.pathname}${url.search}${url.hash}`;
}

interface SmartLinkProps {
  href: string;
  className?: string;
  children: ReactNode;
  /** Tooltip; also used as the accessible name when the text is an image. */
  title?: string;
}

export function SmartLink({ href, className, children, title }: SmartLinkProps) {
  const to = internalPath(href);
  if (to) {
    return (
      <Link to={to} className={className} title={title}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className} title={title}>
      {children}
    </a>
  );
}
